import { z } from 'zod';
import { APP_PLATFORM_CONSTRAINTS, type AppPlatformPackageKind } from '@/lib/constants/appPlatform';
import { AppPlatformUtils, type AppPlatformTitleLimit } from '../AppPlatformUtils';

/** The app name: required, at most the platform's limit (AppPlatformUtils.titleLimit). */
function titleSchema(limit?: AppPlatformTitleLimit) {
  return z
    .string()
    .trim()
    .min(1, 'App name is required')
    .max(limit?.max ?? APP_PLATFORM_CONSTRAINTS.TITLE_MAX, limit?.message);
}

export interface AppPlatformFormSchemaOpts {
  packageKind: AppPlatformPackageKind;
  /** The store's own app id grammar and its sentence (APP_PLATFORM_INFO); absent = the generic check only. */
  storeAppIdPattern?: RegExp;
  storeAppIdPatternMessage?: string;
  isOperator: boolean;
  /** The stored store fields ('' = unset). */
  stored: { storePackageId: string; storeAppId: string };
  /** The platform's app-name limit; absent = TITLE_MAX. */
  titleLimit?: AppPlatformTitleLimit;
}

/**
 * Settings form of one generic platform row. A store field is judged only when
 * the person CHANGED it: a stored value they cannot edit (the assigned package,
 * a recorded store app id on the holder side) must never block their save. The
 * package is an operator's to change (the Override); the store app id is
 * anyone's first set, so its grammar applies to everyone.
 */
export function appPlatformFormSchema(opts: AppPlatformFormSchemaOpts) {
  const { packageKind, storeAppIdPattern, storeAppIdPatternMessage, isOperator, stored, titleLimit } = opts;
  const packageChanged = (v: { storePackageId: string }) =>
    isOperator && v.storePackageId.trim() !== stored.storePackageId.trim();
  const appIdChanged = (v: { storeAppId: string }) => v.storeAppId.trim() !== stored.storeAppId.trim();

  return z
    .object({
      title: titleSchema(titleLimit),
      appStateTypeId: z.number(),
      storePackageId: z.string(),
      storeAppId: z.string(),
    })
    .refine((v) => !packageChanged(v) || AppPlatformUtils.isValidStorePackageId(packageKind, v.storePackageId), {
      path: ['storePackageId'],
      message:
        packageKind === 'android'
          ? 'Enter an Android package name, like com.example.tv (letters, digits and underscores, dot-separated)'
          : 'Enter a bundle id, like com.example.tv',
    })
    .refine((v) => !appIdChanged(v) || AppPlatformUtils.isValidStoreAppId(v.storeAppId), {
      path: ['storeAppId'],
      message: 'Enter the id without spaces',
    })
    .refine(
      (v) =>
        !appIdChanged(v) ||
        // the generic refusal above already speaks for an id the generic check fails
        !AppPlatformUtils.isValidStoreAppId(v.storeAppId) ||
        AppPlatformUtils.isValidStoreAppId(v.storeAppId, storeAppIdPattern),
      {
        path: ['storeAppId'],
        message: storeAppIdPatternMessage ?? 'Enter the id exactly as the store console shows it',
      },
    )
    .refine((v) => !appIdChanged(v) || AppPlatformUtils.appIdHasPackage(v), {
      path: ['storeAppId'],
      message: 'The app needs its package name before the store app id',
    });
}

export type AppPlatformFormSchemaData = z.infer<ReturnType<typeof appPlatformFormSchema>>;

export function appPlatformEnableFormSchema(titleLimit?: AppPlatformTitleLimit) {
  return z.object({ title: titleSchema(titleLimit) });
}

export type AppPlatformEnableFormSchemaData = z.infer<ReturnType<typeof appPlatformEnableFormSchema>>;
